import React, { Component, useState, useMemo } from 'react';
import Pagination from 'components/v2/PaginationV2';
import Tabs from 'components/v2/navs/Tabs';
import {
  extend,
  find,
  has,
  isArray,
  isEmpty,
  isNull,
  isObject,
  map,
  uniq,
  cloneDeep,
} from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { LicenseManager } from 'ag-grid-enterprise';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { isProd, isUS } from 'helpers/helper_functions';
import ColumnsWebAPIUtils from 'utils/ColumnsWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import {
  getShipmentsByStream,
  getShipmentsCount,
  getUVCsvExport,
  sendReport,
} from 'utils/UnifiedViewWebAPIUtils';
import ChangeUserModal from 'components/modules/shipments/ChangeUserModal';
import { massUpdateActions } from 'constants/MassUpdates';
import { useTranslation, withTranslation } from 'react-i18next';
import { getLocaleText } from 'utils/localization/agGridLocale';
import { withLocaleContext } from 'providers/locale-settings-context';
import { Menu, Tab } from '@headlessui/react';
import Panel from 'components/v2/Panel';
import PanelSection from 'components/v2/PanelSection';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import SearchField from 'components/v2/SearchField';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import Alert from 'components/ui/Alert';
import USStates from 'constants/us-states';
import Badge from 'components/v2/Badge';
import DescriptionListItem from 'components/v2/DescriptionListItem';

import { getClassName } from 'utils/ui/classes';
import { useShipperReports } from 'hooks/unifiedview/useReports';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import { sluggify, firstLetterTitlization } from 'utils/localization/utils';
import { getFilter } from 'utils/FiltersWebAPIUtils';
import { getCarrierPreferences } from 'utils/CarrierPreferencesAPI';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import {
  getModelParams,
  loadColumnState,
  transformFilterModelQuery,
} from './transformers';
import {
  getStopPrefixAndField,
  getShipperDrayageColumns,
  getShorthaulColumns,
  getShipperBlacklist,
} from './columns';
import { IMPORT, DOMESTIC } from '../../../config/order-types';
import { TIME_SELECTIONS, STANDARD_REFERENCE_NUMBERS } from './constants';
import { decodeHTMLEncodedString } from '../../../helpers/strings';

import {
  tagFormatter,
  statusFormatter,
  portIntegrationsFormatter,
  orderIDRenderer,
  shipmentIDRenderer,
  marginFormatter,
  deliveryOrderFormatter,
  checkedFormatter,
  lineSeparatedStringFormatter,
} from './formatters';
import TextFilterWithExclude from './customFilters/TextFilterWithExclude';
import TextFilter from './customFilters/TextFilter';
import EmailReportModal from './EmailReporModal';

LicenseManager.setLicenseKey(`${AG_GRID_LICENSE}`);

const ARRIVED_ORDER_TYPES = [IMPORT, DOMESTIC];
const MONTHS_BACK = 12;
const INACTIVE_SHIPMENT_STATUS = ['Job Closed', 'Canceled'];
const POLLING_FREQUENCY = 60000; // 1 min

const drayageOptions = [
  {
    value: 'arrival_date',
    name: 'Arrival Date',
    localizationSlug: 'ARRIVAL_DATE',
  },
  {
    value: 'created_at',
    name: 'Date Created',
    localizationSlug: 'DATE_CREATED',
  },

  {
    value: 'eta',
    name: 'Estimated Arrival',
    localizationSlug: 'ESTIMATED_ARRIVAL',
  },
  {
    value: 'lfd',
    name: 'Terminal Last Free Day',
    localizationSlug: 'TERMINAL_LAST_FREE_DAY',
  },
  {
    value: 'yard_lfd',
    name: 'Yard Last Free Day',
    localizationSlug: 'YARD_LAST_FREE_DAY',
  },
];

const shorthaulOptions = [
  {
    value: 'created_at',
    name: 'Date Created',
    localizationSlug: 'DATE_CREATED',
  },
];

const perPageOptions = [100, 500];

const drayageFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'drayage',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'rail',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

const shorthaulFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'shorthaul',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'longhaul',
      filterType: 'text',
    },
    condition3: {
      type: 'equals',
      filter: 'dayrate',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

const PerPage = (props) => {
  const { t } = useTranslation();
  return (
    (<div
      data-testid="div-1c07f980"
      className="flex items-center my-auto mr-4 space-x-2">
      <label data-testid="label-9d621e01" className="form-label">{t('PER_PAGE')}</label>
      <select
        data-testid="select-78268013"
        onChange={props.onChange}
        value={props.selectedValue}
        className="form-select">
        {props.options.map((item, index) => {
          return (
            (<option data-testid={`option-2720403e-${index}`} key={item} value={item}>
              {item}
            </option>)
          );
        })}
      </select>
    </div>)
  );
};

PerPage.propTypes = {
  onChange: PropTypes.func.isRequired,
  selectedValue: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
};

const CustomDates = (props) => {
  const { t } = useTranslation();
  return (
    (<div data-testid="div-c2161bbd" className="">
      <label data-testid="label-9aa9c6a0" className="form-label">{t('PRIMARY_DATE')}</label>
      <select
        data-testid="select-070cc4fa"
        className="mt-1 form-select"
        onChange={(e) => props.onPrimaryDateChange(e.target.value)}
        value={props.value}>
        {props.options.map((item, i) => {
          return (
            (<option data-testid={`option-1fffebe5-${i}`} key={i} value={item.value}>
              {titleize(t(item.localizationSlug))}
            </option>)
          );
        })}
      </select>
    </div>)
  );
};

CustomDates.propTypes = {
  options: PropTypes.array,
  onPrimaryDateChange: PropTypes.func.isRequired,
};

const sidebarParams = {
  toolPanels: [
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
      toolPanelParams: {
        suppressExpandAll: false,
        suppressFilterSearch: false,
      },
    },
  ],
};
const gridOpts = {
  defaultColDef: {
    enableRowGroup: true,
    enablePivot: false,
    sortable: true,
    resizable: true,
    floatingFilter: true,
    filter: 'agTextColumnFilter',
  },
  rowDragManaged: true,
  copyHeadersToClipboard: true,
  suppressColumnVirtualisation: true,
  suppressScrollOnNewData: true,
};

function ReportTab({ visible, children }) {
  if (!visible) {
    return <Tab.Panel data-testid="TabPanel-8e317a22" />;
  }

  return <Tab.Panel data-testid="TabPanel-7116077a">{children}</Tab.Panel>;
}

export function Dashboard() {
  const [selectedReportIndex, setSelectedReportIndex] = useState(0);

  const userId = SessionStore.getUser()?._id;

  const { isLoading, data: reports } = useShipperReports({ userId });

  function handleTabChange(index) {
    setSelectedReportIndex(index);
  }

  if (isLoading) {
    return <LoadingIndicator dataTestId="LoadingIndicator-565be8b7" />;
  }

  return (
    (<main
      data-testid="main-cce8c1a8"
      className={getClassName('py-5 sm:py-6 flex-1 pl-16')}>
      <div data-testid="div-5111fadd" className="px-4 sm:px-6 lg:px-8">
        <Tabs
          dataTestId="Tabs-a44f1a2f"
          tabs={reports}
          onChange={handleTabChange}
          className="overflow-x-auto">
          <Tab.Panels data-testid="TabPanels-1513b5e9">
            {reports.map((report, i) => (
              <ReportTab key={report._id} visible={selectedReportIndex === i}>
                <Report report={report} />
              </ReportTab>
            ))}
          </Tab.Panels>
        </Tabs>
      </div>
    </main>)
  );
}

class MasterList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showEmailReportModal: false,
      filterModel: {},
      stopGroupStats: {},
      userAllowed: SessionStore.canAccessUnifiedView(), // For now this is limited to admin users
      showBulkUpdateSendDoModal: false,
      send_do: false,
      showChangeCarrierModal: false,
      showChangeShipperModal: false,
      showBulkUpdateCarrierCostModal: false,
      showBulkAccessorialModal: false,
      showBulkBOLGenerationModel: false,
      showChangeConsigneeModal: false,
      showSendRateConfirmationModal: false,
      changeCarrierDisabled: true,
      changeShipperDisabled: true,
      arrivedActionDisabled: true,
      showDrawer: false,
      columnDefs: this.getColumnDefs('drayage', props.t),
      view: 'drayage',
      dateFrom: moment().tz(DEFAULT_TIMEZONE).startOf('day').startOf('year'),
      dateTo: moment().tz(DEFAULT_TIMEZONE).endOf('year'),
      primaryDate: 'created_at',
      quickFilter: '',
      columns: [],
      statusBar: { statusPanels: [{ statusPanel: 'agAggregationComponent' }] },
      frameworkComponents: {
        deliveryOrderFormatter,
        statusFormatter: statusFormatter,
        portIntegrationsFormatter: portIntegrationsFormatter,
        checkedFormatter: checkedFormatter,
        lineSeparatedStringFormatter: lineSeparatedStringFormatter,
        tagFormatter: tagFormatter,
        orderIDRenderer: orderIDRenderer,
        shipmentIDRenderer: shipmentIDRenderer,
        marginFormatter: marginFormatter,
        textFilterWithExclude: TextFilterWithExclude,
        textFilter: TextFilter,
      },
      showSaveFilterModal: false,
      localFilters: [],
      sharedFilters: [],
      filterSelected: false,
      canShare: SessionStore.canShareFilters(),
      shared: false,
      groupDefaultExpanded: 2,
      selectedFilter: {
        name: '',
        id: '',
      },
      order_oid: '',
      showOrderModal: false,
      shipment_oid: '',
      showShipmentModal: false,
      isFirst: true,
      getRowNodeId: function (data) {
        return data._id;
      },
      selectedShipments: [],
      selectedShipmentsForAccessorials: [],
      showBulkEditModal: false,
      isQuickFiltered: false,
      loadingData: true,
      activePage: 1,
      pages: 1,
      queryPayload: {
        params: {},
        options: {
          per_page: 100,
          sort: [{ colId: 'created_at', sort: 'desc' }],
        },
      },
      blacklistedColumns: [],
    };
  }

  abortControllers() {
    // Count and queries abort controllers.
    if (this.abortCountController) {
      if (!this.abortCountController.signal.aborted) {
        this.abortCountController.abort();
      }
    }
    if (this.abortQueryController) {
      if (!this.abortQueryController.signal.aborted) {
        this.abortQueryController.abort();
      }
    }
  }

  getSelectedRows() {
    if (!this.api) {
      return [];
    }
    return this.api.getSelectedRows().map((s) => ({
      ...s,
      _id: s.shipment_oid,
      id: s.shipment_oid,
    }));
  }

  onHideShipmentModal = () => {
    this.setState({
      showShipmentModal: false,
    });
  };

  onHideModal = () => {
    this.setState({
      showOrderModal: false,
    });
  };

  getModelParams() {
    const { view } = this.state;
    const filterModel = cloneDeep(this.api.getFilterModel());

    return getModelParams({
      view,
      filterModel,
      agGridApi: this.api,
    });
  }

  transformFilterModelQuery() {
    const modelParams = this.getModelParams();
    return transformFilterModelQuery(modelParams);
  }

  onQuickFilter(quickFilter) {
    this.setState(
      { isQuickFiltered: !isEmpty(quickFilter), quickFilter },
      () => {
        this.api.setQuickFilter(quickFilter);
      },
    );
  }

  onGridReady = async (params) => {
    this.api = params.api;
    this.columnApi = params.columnApi;
    this.api.showLoadingOverlay();
    this.api.autoSizeColumns();

    this.api.addEventListener('modelUpdated', () => {
      this.handleRowDisplayed();
    });

    this.api.addEventListener('columnVisible', this.onColumnVisible);
    this.api.addEventListener('filterChanged', this.onFilterChange);
    this.api.addEventListener('sortChanged', this.onSortChanged);
    this.api.addEventListener('cellEditingStopped', this.onEditingStopped);
    this.getFilterResults();
  };

  onEditingStopped = (event) => {
    if (!event.valueChanged || !event.colDef.updateFunc) {
      return;
    }

    event.colDef.updateFunc(event);
  };

  onColumnVisible = (event) => {
    const { visible, column } = event;

    const colId = column?.colId;

    if (!colId) {
      return;
    }

    const res = getStopPrefixAndField(colId);
    if (!res.isStopGroupField) {
      return;
    }
    const count = this.state.stopGroupStats[res.prefix];

    if (count === 1) {
      return;
    }

    const { prefix, field } = res;

    const otherColumns = Array.from(Array(count).keys())
      .filter((index) => {
        const stopGroupField = `${prefix}__${index}__${field}`;
        if (colId === stopGroupField) {
          return false;
        }

        const col = this.columnApi.getColumn(stopGroupField);
        if (col.visible === visible) {
          return false;
        }
        return true;
      })
      .map((index) => {
        const colId = `${prefix}__${index}__${field}`;

        return {
          colId,
          hide: !visible,
        };
      });

    if (otherColumns.length === 0) {
      return;
    }

    this.api.applyColumnState({ state: otherColumns, applyOrder: true });
  };

  onFilterChange = (e) => {
    if (this.state.isQuickFiltered) return;

    this.handlePagination(1);
  };

  onSortChanged = () => {
    if (this.state.isQuickFiltered) return;

    this.getFilterResults();
  };

  onHideBulkEditModal = () => this.setState({ showBulkEditModal: false });

  primaryDateChanged = (primaryDate) => {
    this.setState({ primaryDate }, () => {
      this.updateDateFilterModel();
    });
  };

  getDateOptions = () => {
    return [...drayageOptions, ...shorthaulOptions].map((opt) => opt.value);
  };

  removePrimaryDateFields(currentFilterModel) {
    // remove the existing 'date filter' if one exists
    const modelKeys = Object.keys(currentFilterModel) || [];

    const primaryDateOptions = this.getDateOptions();

    const model = modelKeys.reduce((model, key) => {
      if (primaryDateOptions.includes(key)) {
        delete model[key];
      }
      return model;
    }, currentFilterModel);
    return model;
  }

  getPrimaryDateFilterModel = ({ dateFrom, dateTo, primaryDate }) => {
    const currentFilterModel = this.api.getFilterModel() || {};
    const sameDay = moment(dateTo).isSame(moment(dateFrom, 'day'));
    const filterModel = {
      dateTo: moment(dateTo).endOf('day').format('YYYY-MM-DD'),
      dateFrom: moment(dateFrom).startOf('day').format('YYYY-MM-DD'),
      type: sameDay ? 'equals' : 'inRange',
      filterType: 'date',
    };

    const model = this.removePrimaryDateFields(currentFilterModel);
    model[primaryDate] = filterModel;

    return model;
  };

  /*
   * updateDateFilterModel deletes previous date and injects new
   */
  updateDateFilterModel = () => {
    const { dateFrom, dateTo, primaryDate } = this.state;
    const model = this.api.getFilterModel();
    const filterInstance = this.api.getFilterInstance(this.state.primaryDate);
    const sameDay = moment(dateTo).isSame(moment(dateFrom, 'day'));
    const filterModel = {
      dateTo: moment(dateTo).endOf('day').toISOString(),
      dateFrom: moment(dateFrom).startOf('day').toISOString(),
      type: sameDay ? 'equals' : 'inRange',
      filterType: 'date',
    };
    // remove the existing 'date filter' if one exists
    if (model) {
      const primaryDateOptions = this.getDateOptions();
      const modelKeys = Object.keys(model) || [];
      modelKeys.forEach((key) => {
        if (primaryDateOptions.includes(key)) {
          delete model[key];
        }
      });
      model[primaryDate] = filterModel;
    }

    if (filterInstance) {
      this.makeDateFilter({
        dateTo: moment(dateTo).endOf('day').format('YYYY-MM-DD'),
        dateFrom: moment(dateFrom).startOf('day').format('YYYY-MM-DD'),
        type: sameDay ? 'equals' : 'inRange',
        filterInstance,
      });
    }
    this.getFilterResults();
  };

  /*
   * makeDateFilters is a helper method to set the date value
   * @params { Object } opt deconstructs to set datefilter
   * @returns void
   */
  makeDateFilter = (opt) => {
    const { dateFrom, dateTo, filterInstance, type } = opt;

    const model = {
      dateTo,
      dateFrom,
      type,
      filterType: 'date',
    };

    filterInstance.setModel(model);
  };

  handleFiltersUpdate = (update) => {
    const { filters, event } = update;
    const localFilters = filters
      .filter((filter) => filter.shared !== true)
      .sort((a, b) => (a?.name?.trim() || '').localeCompare(b.name.trim()));
    const sharedFilters = filters
      .filter((filter) => filter.shared === true)
      .sort((a, b) => (a?.name?.trim() || '').localeCompare(b?.name?.trim()));
    if (event === 'CREATED') {
      const {
        result: { shared, _id },
      } = update;
      const cols = [];
      this.api.getAllGridColumns().forEach((col) => {
        const { userProvidedColDef, visible } = col;
        if (!isNull(userProvidedColDef)) {
          const { field } = userProvidedColDef;
          if (visible) {
            cols.push(field);
          }
        }
      });
      const filter_id = _id;
      if (!shared) {
        ColumnsWebAPIUtils.createCols({ filter_id, cols });
      }
    }

    this.setState({
      localFilters,
      sharedFilters,
    });
  };

  handleRowDisplayed = () => {
    this.setState({ rowCount: this.api.getDisplayedRowCount() });
  };

  handleFiltersStoreUpdate = (row) => {
    const { add, update } = row;

    if (this.state.isFirst) {
      this.api.batchUpdateRowData({ add });
      this.setState({
        isFirst: false,
      });
    } else {
      if (add.length > 0) {
        this.api.batchUpdateRowData({ add });
        this.handleRowDisplayed();
      }
      if (update.length > 0) {
        this.api.batchUpdateRowData({ update });
      }
    }
  };

  getPrimaryDateFilterFields({ filter, state }) {
    let dateFrom;
    let dateTo;
    const { timeSelected } = filter;

    let decodedTimeSelected;
    const primaryDate = filter.primaryDate || 'created_at';

    if (timeSelected) {
      decodedTimeSelected = decodeHTMLEncodedString(timeSelected);
      const quickTimeSelected = TIME_SELECTIONS.find(
        (t) => t.name === decodedTimeSelected,
      );
      if (quickTimeSelected) {
        dateFrom = quickTimeSelected.start();
        dateTo = quickTimeSelected.end();
      }
    } else {
      decodedTimeSelected = '';
      dateFrom = state.dateFrom;
      dateTo = state.dateTo;
    }

    const primaryDateFilterModel = this.getPrimaryDateFilterModel({
      primaryDate,
      dateFrom,
      dateTo,
    });

    return {
      primaryDate,
      dateFrom,
      dateTo,
      timeSelected: decodedTimeSelected,
      showDrawer: !!timeSelected,
      model: primaryDateFilterModel,
    };
  }

  getSavedFilterModel = (filter) => {
    if (!filter.model) {
      return {};
    }
    const mappedFilterModel = Object.keys(filter.model).reduce((model, key) => {
      const value = filter.model[key];
      model[key] = value;

      // remove empty filters
      if (Array.isArray(value.value) && value.value.length === 0) {
        delete model[key];
      }
      return model;
    }, {});

    return mappedFilterModel;
  };

  getPayload = () => {
    const params = this.transformFilterModelQuery(this.getModelParams());

    const options = {
      sort: this.getSortModel(),
    };
    const sameDay = moment(this.state.dateTo).isSame(
      moment(this.state.dateFrom, 'day'),
    );

    const columns = this.api
      .getAllGridColumns()
      .filter((c) => c.visible && c.colDef.field)
      .map((c) => {
        // BE expecting field for data mapping and actual headerName for csv header
        return {
          field: c.colDef.field,
          headerName: c.colDef.headerName,
        };
      });
    return {
      params: {
        [this.state.primaryDate]: {
          dateFrom: this.state.dateFrom,
          dateTo: this.state.dateTo,
          type: sameDay ? 'equals' : 'inRange',
          filterType: 'date',
        },
        ...params,
      },
      options,
      columns,
    };
  };

  exportCsv = async () => {
    this.setState({ exportLoading: true }, async () => {
      await getUVCsvExport({
        ...this.getPayload(),
      });
      this.api.hideOverlay();
      this.setState({ exportLoading: false });
    });
  };

  getColumnState() {
    const columnState = this.api.getColumnState();
    const pinned = columnState
      .filter((c) => c.pinned)
      .map((c) => ({ field: c.colId, pinned: c.pinned }));
    const hidden = columnState.filter((c) => c.hide).map((c) => c.colId);
    const columnOrder = columnState.map((c) => c.colId);

    return { columnOrder, pinned, hidden };
  }

  handleRowSelection = () => {
    const selectedRows = this.api.getSelectedRows();
    let arrivedActionDisabled = true;
    let changeCustomerDisabled = false;

    if (selectedRows.length) {
      arrivedActionDisabled = !selectedRows.every(
        (s) => !!s.order_oid && ARRIVED_ORDER_TYPES.includes(s.order_type),
      );
      changeCustomerDisabled = selectedRows.some((s) =>
        INACTIVE_SHIPMENT_STATUS.includes(s.shipmentStatus),
      );
      const changeCarrierDisabled =
        !this.areShipmentsWithSameShipper(selectedRows);
      const selectedShipments = uniq(
        selectedRows.filter((s) => !!s.shipment_oid),
      );

      const hasEquipment = (shipment) => {
        return (
          shipment.stops.filter((s) => {
            return s.stop_equipment_oid !== '';
          }).length > 0
        );
      };

      this.setState({
        arrivedActionDisabled,
        changeCarrierDisabled: changeCustomerDisabled || changeCarrierDisabled,
        changeShipperDisabled: changeCustomerDisabled,
        selectedShipments,
      });
    } else {
      this.setState({
        arrivedActionDisabled,
        changeCarrierDisabled: changeCustomerDisabled,
        changeShipperDisabled: true,
        selectedShipments: [],
      });
    }
  };

  getDataTotal = () => {
    this.setState(async (prevState) => {
      const sameDay = moment(this.state.dateTo).isSame(
        moment(this.state.dateFrom, 'day'),
      );

      this.abortCountController = new AbortController();

      const totalCount = await getShipmentsCount(
        {
          params: {
            [prevState.primaryDate]: {
              dateFrom: prevState.dateFrom,
              dateTo: prevState.dateTo,
              type: sameDay ? 'equals' : 'inRange',
              filterType: 'date',
            },
            ...prevState.queryPayload.params,
          },
        },
        { controller: this.abortCountController },
      );
      const pages = Math.ceil(
        totalCount / prevState.queryPayload.options.per_page,
      );

      this.setState({ totalCount, pages });
    });
  };

  transformDateCondition(model = {}) {
    if (model.filterType === 'date') {
      const conditionsThatUseStartOfDayOnly = [
        'equals',
        'notEqual',
        'before',
        'after',
      ];

      if (conditionsThatUseStartOfDayOnly.includes(model.type)) {
        const date = moment(model.dateFrom);
        model.dateFrom = date.clone().startOf('day').toISOString();
        model.dateTo = date.clone().endOf('day').toISOString();
      } else {
        model.dateFrom = moment(model.dateFrom).startOf('day').toISOString();
        model.dateTo = moment(model.dateTo).endOf('day').toISOString();
      }
    }
    return model;
  }

  getSortModel = () => {
    return this.api.getColumnState().filter((col) => !!col.sort);
  };

  /*
   * multiLineFilter is used to create a query string for multi-line items
   * @parms{Object} filterModel is the actual filter
   * @params{String} field is the name of the field
   * @returns an object with conditions for each word to be queried
   * */
  makeMultiItemFilter(field, filterModel) {
    const conditionMap = filterModel.reduce((map, val, i) => {
      map[`condition${i + 1}`] = {
        filter: val[field],
        type: 'contains',
        filterType: 'text',
      };
      return map;
    }, {});

    conditionMap.operator = 'OR';
    return {
      [field]: conditionMap,
    };
  }

  getFilterResults = async () => {
    const {
      activePage: page,
      queryPayload: {
        options: { per_page },
      },
    } = this.state;

    const dateFilter = this.api.getFilterModel();

    if (this.api) {
      this.api.showLoadingOverlay();

      // make the query string for the shared/pesonal filters
      let params = this.transformFilterModelQuery(
        this.getModelParams(dateFilter),
      );
      const options = {
        sort: this.getSortModel(),
        per_page,
        page,
      };

      const sameDay = moment(this.state.dateTo).isSame(
        moment(this.state.dateFrom, 'day'),
      );

      // Persisted saved filters have columns that don't exist anymore.
      let additional = {};
      const BLACKLIST = [
        'stop_type',
        'scheduled',
        'pickup_start',
        'pickup_end',
        'deliver_start',
        'deliver_end',
      ];
      if (!BLACKLIST.includes(this.state.primaryDate)) {
        additional[this.state.primaryDate] = {
          dateFrom: this.state.dateFrom,
          dateTo: this.state.dateTo,
          type: sameDay ? 'equals' : 'inRange',
          filterType: 'date',
        };
      }
      // Persisted saved filters have columns that are no longer present
      const columnDefs = this.api.getColumnDefs();
      /*
      const activeCols = columnDefs.map((c) => {
        return c.colId;
      });
      */
      params = _.omit(params, BLACKLIST);

      this.setState(
        (previousState) => {
          return {
            ...previousState,
            primaryDate: BLACKLIST.includes(previousState.primaryDate)
              ? 'created_at'
              : previousState.primaryDate,
            loadingData: true,
            activePage: page || 1,
            queryPayload: {
              ...previousState.queryPayload,
              params,
              options,
            },
          };
        },
        async () => {
          // Any pending requests should abort.
          this.abortControllers();

          this.abortQueryController = new AbortController();

          // Get Count
          this.getDataTotal();

          // Get Query Results
          const data = await getShipmentsByStream(
            {
              params: {
                ...additional,
                ...params,
              },
              options,
            },
            {
              controller: this.abortQueryController,
            },
          );
          this.handleDataStream(data);
        },
      );
    }
  };

  handleDataStream = async (data) => {
    try {
      const { items, stats: stopGroupStats } = data;

      this.setState(
        () => {
          const updates = {
            stopGroupStats,
            items,
            loadingData: false,
          };

          return updates;
        },
        () => {
          this.api.hideOverlay();
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  handlePerPageChange = (per_page) => {
    const { queryPayload } = this.state;
    const { options: currentOptions } = queryPayload;

    this.setState(
      {
        activePage: 1,
        queryPayload: {
          ...queryPayload,
          options: {
            ...currentOptions,
            per_page: parseInt(per_page, 10),
          },
        },
      },
      () => this.getFilterResults(),
    );
  };

  handlePagination = (page) => {
    this.setState({ activePage: page }, () => {
      this.getFilterResults();
    });
  };

  renderPagination() {
    return (
      (<span
        data-testid="span-c0a749cd"
        className="flex justify-between lg:space-x-8 items-center">
        <Pagination
          dataTestId="Pagination-22e5e778"
          first
          last
          total={this.state.totalCount}
          page={this.state.activePage}
          onPageClick={(page) => this.handlePagination(page)}
          perPage={this.state.queryPayload.options.per_page} />
        <PanelSection dataTestId="PanelSection-b70aa0b5">
          <PerPage
            options={perPageOptions}
            onChange={(e) => this.handlePerPageChange(e.target.value)}
            selectedValue={this.state.queryPayload.options.per_page}
          />
        </PanelSection>
      </span>)
    );
  }

  areShipmentsWithSameShipper = (selectedRows) => {
    if (selectedRows.length === 1) {
      return true;
    }
    return (
      selectedRows[0]?.shipper_oid &&
      selectedRows.every((shipment) => {
        return shipment.shipper_oid === selectedRows[0].shipper_oid;
      })
    );
  };

  handleTimePickerReset = () => {
    this.setState({
      showDrawer: false,
      timeSelected: '',
      dateFrom: moment().startOf('day').subtract(MONTHS_BACK, 'months'),
      dateTo: moment().endOf('year'),
    });
  };

  handleShowEmailReporModal = () => {
    this.setState({ showEmailReportModal: true });
  };

  handleHideEmailReportModal = () => {
    this.setState({ showEmailReportModal: false });
  };

  async componentDidMount() {
    await this.setColumnBlacklist();
    await this.handleFilterSelect();
    this.startPolling();
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling() {
    this.pollingTimer = setInterval(
      () => this.getFilterResults(true),
      POLLING_FREQUENCY,
    );
  }

  stopPolling() {
    clearInterval(this.pollingTimer);
  }

  async setColumnBlacklist() {
    const { t } = this.props;
    const { blacklistedColumns } = await getShipperBlacklist(t);
    this.setState({ blacklistedColumns });
  }

  handleFilterSelect = async () => {
    const selectedFilter = this.props.report;
    let filter = {};
    // get full filter model from the BE
    const result = await getFilter(selectedFilter._id);

    if (result) {
      filter = result.entity;

      let mappedColumns;
      this.setState(
        (prevState) => {
          const { columns } = prevState;
          const columnSet = find(columns, (col) => {
            return col.filter_id === filter._id;
          });

          mappedColumns = this.getColumnDefs(filter.view, this.props.t);
          const mappedFilterModel = this.getSavedFilterModel(filter);

          return {
            view: filter.view,
            selectedFilter: filter,
            filterSelected: true,
            activeCols: columnSet ? columnSet._id : null,
            applyingSavedFilters: true,
            columnDefs: mappedColumns,
            filterModel: { ...mappedFilterModel },
          };
        },
        async () => {
          this.api.autoSizeColumns();
          await this.api.setGridOption('columnDefs', mappedColumns);
          loadColumnState(filter, this.api, this.state.blacklistedColumns);
          this.setCustomFilterModels(this.state.filterModel);
          this.api.setFilterModel(this.state.filterModel);
          this.api.onFilterChanged();
          if (filter.rowGroupColumns && filter.rowGroupColumns?.length > 0) {
            this.api.setRowGroupColumns(filter.rowGroupColumns);
          }
        },
      );
    }
  };

  handleOpenCarrierChangeModal = () => {
    this.setState({ changeCarrierModalVisible: true });
  };

  handleCloseCarrierChangeModal = () => {
    this.setState({ changeCarrierModalVisible: false });
  };

  handleChangeCarrier = async (carrier, selectedJobs) => {
    const isNetworkCarrier = carrier.name === 'Cargomatic Network Carrier';
    const jobIds = selectedJobs.map((j) => j.shipment_oid);
    await ShipmentWebAPIUtils.shipperAssignCarrier(
      carrier?.value?._id || null,
      jobIds,
      isNetworkCarrier,
    );
  };

  render() {
    const { t } = this.props;
    const { blacklistedColumns } = this.state;

    if (!blacklistedColumns) {
      return null;
    }

    return (<>
      <Panel dataTestId="Panel-186496b7">
        <PanelSection dataTestId="PanelSection-40f76544">
          <div
            data-testid="div-7601e8aa"
            className="flex justify-between flex-wrap items-center">
            <div data-testid="div-cc309652" className="flex-1" />
            <div data-testid="div-32eea091" className="flex space-x-4 items-center">
              {/*
            <CustomDropdown
              value={options.find((opt) => opt.value === this.state.view)}
              options={options}
              onChange={this.handleViewChange}
            />

            */}
              {!isProd() && (
                <DropdownMenu dataTestId="DropdownMenu-af1a2935" title="Actions">
                  <Menu.Item data-testid="MenuItem-07553728" key="change-carrier">
                    <DropdownItem
                      dataTestId="change-carrier-button"
                      onClick={this.handleOpenCarrierChangeModal}
                      disabled={
                        !this.state.selectedShipments ||
                        !this.state.selectedShipments.length
                      }
                    >
                      Change Carrier
                    </DropdownItem>
                  </Menu.Item>
                </DropdownMenu>
              )}
              <DropdownMenu dataTestId="DropdownMenu-3d86619c" useIcon>
                {!isProd() && (
                  <Menu.Item data-testid="MenuItem-521fcda4" key="save_new_report">
                    <DropdownItem
                      useIcon
                      dataTestId="deleteMarketRateButton"
                      onClick={this.handleShowEmailReporModal}
                    >
                      Email Report
                    </DropdownItem>
                  </Menu.Item>
                )}
                <Menu.Item data-testid="MenuItem-bf280307" key="save_new_report">
                  <DropdownItem
                    useIcon
                    dataTestId="deleteMarketRateButton"
                    onClick={this.exportCsv}
                  >
                    Export CSV
                  </DropdownItem>
                </Menu.Item>
              </DropdownMenu>
            </div>
          </div>
        </PanelSection>
        <div
          data-testid="div-8c0a0f96"
          className="mt-2 ag-theme-balham"
          style={{ height: 700 }}>
          <AgGridReact
            rowData={this.state.items}
            immutableData
            maintainColumnOrder
            rememberGroupStateWhenNewData
            animateRows
            getRowId={(params) => params.data._id}
            suppressClickEdit
            suppressRowClickSelection
            enableCellTextSelection
            enableRangeSelection
            rowSelection="multiple"
            getRowNodeId={this.state.getRowNodeId}
            frameworkComponents={this.state.frameworkComponents}
            multiSortKey="ctrl"
            onGridReady={this.onGridReady}
            onSortChanged={this.handleTimePickerReset}
            statusBar={this.state.statusBar}
            columnDefs={this.state.columnDefs}
            defaultColDef={gridOpts.defaultColDef}
            onRowSelected={this.handleRowSelection}
            localeText={getLocaleText()}
            overlayLoadingTemplate={`<div><span class="text-base text-green font-bold">${t(
              'LOADING',
            )}</span></div>`}
            sideBar={null}
            allowDragFromColumnsToolPanel
          />
        </div>
        {this.renderPagination()}
      </Panel>
      {this.state.showEmailReportModal && (
        <EmailReportModal
          report={this.props.report}
          isOpen={this.state.showEmailReportModal}
          onClose={this.handleHideEmailReportModal}
          payload={this.getPayload()}
        />
      )}
      {this.state.changeCarrierModalVisible && (
        <ChangeUserModal
          jobs={this.state.selectedShipments.map((j) => ({
            shipper: {
              id: j.shipper_oid || undefined,
            },
            ...j,
          }))}
          show={this.state.changeCarrierModalVisible}
          type="carrier"
          onSave={this.handleChangeCarrier}
          onHide={this.handleCloseCarrierChangeModal}
          isShipper
        />
      )}
    </>);
  }
  getColumnDefs(view, t) {
    const columns =
      view === 'drayage' ? getShipperDrayageColumns() : getShorthaulColumns();

    return columns
      .map((incoming) => {
        const column = { ...incoming };
        column.menuTabs = ['filterMenuTab', 'generalMenuTab'];

        if (column.field === 'order_id') {
          column.cellRendererParams = {
            innerRenderer: (props) => {
              return props.value;
            },
            onOrderClick: (data) => {
              const { order_oid } = data;
              this.setState({
                order_oid,
                showOrderModal: true,
              });
            },
          };
        } else if (column.field === 'shipment_id') {
          column.cellRendererParams = {
            innerRenderer: (props) => {
              return props.value;
            },
            onShipmentClick: (data) => {
              const { shipment_oid } = data;
              this.setState({
                shipment_oid,
                showShipmentModal: true,
              });
            },
          };
        }

        if (
          isUS() &&
          column.field &&
          column.field.toLowerCase().includes('state')
        ) {
          column.filter = 'agSetColumnFilter';
          column.floatingFilter = true;
          column.filterParams = {
            values: USStates.map((state) => state.abbreviation),
          };
        }
        return column;
      })
      .map((c) => {
        if (c.field === 'margin') {
          c.cellRenderer = 'marginFormatter';
        }
        c.headerName = c.titleize
          ? firstLetterTitlization(t(sluggify(c.headerName)))
          : t(sluggify(c.headerName));
        return c;
      });
  }
  setCustomFilterModels = (savedFilterModel) => {
    const customFilterColumns = [
      'shipment_id',
      'order_id',
      'orderStatus',
      'chassis_number',
      'shipper_name',
      'reference_numbers',
      ...STANDARD_REFERENCE_NUMBERS,
    ];

    const stopColumnFields = this.state.columnDefs
      .filter((c) => c.children)
      .flatMap((c) => c.children)
      .map((c) => c.field);
    const customFilterStopColumns = stopColumnFields.filter(
      (f) =>
        f &&
        (f.endsWith('stop_equipment_id') || f.endsWith('stop_chassis_number')),
    );

    const customFilterFields = [
      ...customFilterColumns,
      ...customFilterStopColumns,
    ];

    customFilterFields.forEach((field) => {
      const filterInstance = this.api.getFilterInstance(field);
      if (!isEmpty(filterInstance)) {
        filterInstance.setModel(
          savedFilterModel && savedFilterModel[field]
            ? savedFilterModel[field]
            : null,
        );
      }
    });
  };
}
const Report = withTranslation()(withLocaleContext(MasterList));
